<template>
  <div>
    <v-container>
      <div class="mb-3">
        <h3 class="headline">Survey by Agents</h3>
        <div class="py-3">
          <DashboardSelectChannel v-model="channel" @changeChannel="handleFetchData" />
        </div>
        <v-layout align-center wrap>
          <v-flex xs12 class="pa-2 text-xs-right">
            <v-btn dark depressed color="primary" @click="showAdvancedSearch = !showAdvancedSearch">
              <v-icon>search</v-icon>
              &nbsp;
              {{ $t('advanced search') }}&nbsp;
              <em v-if="!showAdvancedSearch" class="fas fa-angle-down" />
              <em v-else class="fas fa-angle-up" />
            </v-btn>

            <v-btn outline color="primary" @click="handleExportButtonClick">
              <em class="fas fa-file-export mr-1" />
              {{ $t('export') }}
            </v-btn>
          </v-flex>
        </v-layout>
      </div>
      <div v-if="showAdvancedSearch" class="mb-4 py-2">
        <AdvancedSearch v-model="filter" @onSearch="handleSearch" />
      </div>

      <v-data-table
        disable-initial-sort
        :headers="tableHeaders"
        hide-actions
        :items="dataItems"
        :pagination="pagination"
        class="elevation-1 mt-3"
      >
        <template #items="{ item }">
          <td>{{ item.customer_name }}</td>
          <td>{{ item.agent }}</td>
          <td>{{ item.ticket_id }}</td>
          <td>{{ item.survey_sent_timestamp | DateFormat('DD/MM/YYYY HH:mm:ss') }}</td>
          <td>
            <template v-if="item.survey_submit_timestamp">
              {{ item.survey_submit_timestamp | DateFormat('DD/MM/YYYY HH:mm:ss') }}
            </template>
            <template v-else>-</template>
          </td>
          <td>{{ item.page }}</td>
          <td>
            <ChannelLabel :channel="item.channel">
              <template #channel="{ channelColor, selectedChannel }">
                <span v-if="selectedChannel" :style="{ color: channelColor }">
                  <em v-if="selectedChannel.icon" :class="selectedChannel.icon" />
                  {{ selectedChannel.text }}
                </span>
              </template>
            </ChannelLabel>
          </td>
          <template v-if="isEmmaClinic">
            <td>คุณพึงพอใจในการให้บริการของแอดมินในครั้งนี้อยู่ในระดับใด</td>
            <td>
              {{
                item.survey_data && item.survey_data.question1 > 0
                  ? item.survey_data.question1
                  : '-'
              }}
            </td>
            <td>คุณพึงพอใจในการตอบคำถามได้อย่างครบถ้วนในระดับใด</td>
            <td>
              {{
                item.survey_data && item.survey_data.question2 > 0
                  ? item.survey_data.question2
                  : '-'
              }}
            </td>
            <td>
              {{
                item.survey_data && item.survey_data.suggestion ? item.survey_data.suggestion : '-'
              }}
            </td>
          </template>
          <template v-else>
            <td>{{ item.osat_group || '-' }}</td>
            <td>{{ item.osat_score > 0 ? item.osat_score : '-' }}</td>
            <td>{{ item.nps_group || '-' }}</td>
            <td>{{ item.nps_score > 0 ? item.nps_score : '-' }}</td>
          </template>
          <td>{{ item.verbatim || '-' }}</td>
        </template>
      </v-data-table>

      <v-layout class="mt-2">
        <v-spacer></v-spacer>
        <v-pagination
          v-model="pageNumber"
          :total-visible="6"
          :length="pagelength"
          @input="handleChangePage"
        ></v-pagination>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import surveyApi from '@/api/survey/survey'
import { mapGetters } from 'vuex'
import XLSX from 'xlsx'
import AdvancedSearch from './AdvancedSearch.vue'
import DashboardSelectChannel from '@/components/view/dashboard/DashboardSelectChannel'
import dayjs from 'dayjs'
import { DATETIME } from '@/constants/datetime'
import ChannelLabel from '@/components/elements/ChannelLabel'
import { PAGE_GROUP } from '@/constants/page'

export default {
  name: 'SurveyComponent',
  components: {
    AdvancedSearch,
    DashboardSelectChannel,
    ChannelLabel
  },
  data() {
    return {
      limit: 10,
      offset: 0,
      pagination: { rowsPerPage: -1, sortBy: '' },
      total: 0,
      pageTemp: 1,
      pageNumber: 1,
      dataItems: [],
      localPagination: {
        rowsPerPage: 10,
        ...this.pagination
      },
      headers: [
        { text: 'Customer name', value: 'customerName', sortable: false },
        { text: 'Agent', value: 'agent', sortable: false },
        { text: 'Ticket no.', value: 'ticketNo', sortable: false },
        { text: 'Survey sent date & time', value: 'surveySentDatetime', sortable: false },
        { text: 'Survey summit date & time', value: 'surveySummitDatetime', sortable: false },
        { text: 'Page', value: 'page', sortable: false },
        { text: 'Channel', value: 'channel', sortable: false },
        { text: 'Group OSAT (ANS)', value: 'groupOsatAns', sortable: false },
        { text: 'OSAT', value: 'osat', sortable: false },
        { text: 'Group NPS (ANS)', value: 'groupNpsAns', sortable: false },
        { text: 'NPS', value: 'nps', sortable: false },
        { text: 'Verbatim', value: 'verbatim', sortable: false }
      ],
      filter: {},
      showAdvancedSearch: false,
      filterPage: null,
      channel: 'all',
      isExport: false
    }
  },
  computed: {
    ...mapGetters(['selectedBot']),
    pagelength() {
      if (this.total) {
        const total = parseFloat(this.total)
        return Math.ceil(total / this.limit)
      } else {
        return 1
      }
    },
    childPageOptions() {
      return this.$store.state.ttbChildPages
    },
    tableHeaders() {
      if (PAGE_GROUP.EMMA_CLINIC.includes(this.selectedBot.page_group)) {
        return [
          { text: 'Customer name', value: 'customerName', sortable: false },
          { text: 'Agent', value: 'agent', sortable: false },
          { text: 'Ticket no.', value: 'ticketNo', sortable: false },
          { text: 'Survey sent date & time', value: 'surveySentDatetime', sortable: false },
          { text: 'Survey summit date & time', value: 'surveySummitDatetime', sortable: false },
          { text: 'Page', value: 'page', sortable: false },
          { text: 'Channel', value: 'channel', sortable: false },
          { text: 'Question 1', value: 'question1', sortable: false },
          { text: 'Score', value: 'score', sortable: false },
          { text: 'Question 2', value: 'question2', sortable: false },
          { text: 'Score', value: 'score', sortable: false },
          { text: 'Suggestion', value: 'suggestion', sortable: false },
          { text: 'Verbatim', value: 'verbatim', sortable: false }
        ]
      } else {
        return this.headers
      }
    },
    isEmmaClinic() {
      return PAGE_GROUP.EMMA_CLINIC.includes(this.selectedBot.page_group)
    }
  },
  mounted() {
    this.handleFetchData()

    if (!this.childPages) {
      this.$store.dispatch('loadChildPages')
    }
  },
  methods: {
    async handleFetchData() {
      const vm = this
      try {
        vm.$store.dispatch('loading')
        const resposnse = await vm.getTicketSurvey()
        vm.$store.commit('CLEAR_LOADING')
        if (resposnse && resposnse.status === 200) {
          vm.dataItems = resposnse.data.data
          vm.total = resposnse.data.total
        }
      } catch (error) {
        vm.$store.commit('CLEAR_LOADING')
        console.error(error)
      }
    },
    async getTicketSurvey() {
      const vm = this
      try {
        const data = {
          page_id: vm.selectedBot.pageid,
          select_page_id: vm.filterPage ? vm.filterPage.pageid : null,
          channel: vm.channel,
          limit: vm.isExport ? vm.total : vm.limit,
          offset: vm.offset
        }

        if (this.filter.agent) {
          Object.assign(data, { agent: this.filter.agent })
        }
        if (this.filter.ticket_id) {
          Object.assign(data, { ticket_id: this.filter.ticket_id })
        }
        // if (this.filter.nps_score) {
        //   Object.assign(data, { nps_score: this.filter.nps_score })
        // }
        // if (this.filter.osat_score) {
        //   Object.assign(data, { osat_score: this.filter.osat_score })
        // }
        if (this.filter.survey_sent_from) {
          Object.assign(data, {
            survey_sent_from: dayjs(this.filter.survey_sent_from).format(DATETIME.DATE_FORMAT)
          })
        }
        if (this.filter.survey_sent_to) {
          Object.assign(data, {
            survey_sent_to: dayjs(this.filter.survey_sent_to).format(DATETIME.DATE_FORMAT)
          })
        }
        if (this.filter.survey_submit_from) {
          Object.assign(data, {
            survey_submit_from: dayjs(this.filter.survey_submit_from).format(DATETIME.DATE_FORMAT)
          })
        }
        if (this.filter.survey_submit_to) {
          Object.assign(data, {
            survey_submit_to: dayjs(this.filter.survey_submit_to).format(DATETIME.DATE_FORMAT)
          })
        }

        const response = await surveyApi.getSurvey({ params: data })

        if (response) {
          return response
        } else {
          return null
        }
      } catch (error) {
        console.error(error)
        return null
      }
    },
    handleSearch() {
      this.offset = 0
      this.pageTemp = 1
      this.pageNumber = 1
      this.handleFetchData()
    },
    handleChangePage(val) {
      const vm = this
      if (vm.pageTemp !== val) {
        vm.pageTemp = val
        vm.offset = vm.pageNumber * vm.limit - vm.limit
        vm.handleFetchData()
      }
    },
    async handleExportButtonClick() {
      const vm = this
      try {
        vm.isExport = true
        const response = await vm.getTicketSurvey()
        const requestData = response.data.data
        vm.isExport = false

        if (requestData == null) return
        const requests = requestData.map((el) => {
          return {
            'Survey Metadata - Start Date': el.survey_sent_timestamp
              ? dayjs(el.survey_sent_timestamp).format('DD/MM/YYYY HH:mm')
              : '-',
            'Survey Metadata - End Date': el.survey_submit_timestamp
              ? dayjs(el.survey_submit_timestamp).format('DD/MM/YYYY HH:mm')
              : '-',
            'Survey Metadata - Recorded Date': el.survey_submit_timestamp
              ? dayjs(el.survey_submit_timestamp).format('DD/MM/YYYY HH:mm')
              : el.survey_sent_timestamp
              ? dayjs(el.survey_sent_timestamp).format('DD/MM/YYYY HH:mm')
              : '-',

            'Customer Name': el.customer_name,
            'Distribution Channel': el.page,
            Language: el.lang ? el.lang.toUpperCase() : '-',
            ...(this.isEmmaClinic
              ? {
                  'Question 1': 'คุณพึงพอใจในการให้บริการของแอดมินในครั้งนี้อยู่ในระดับใด',
                  'Score - Question 1':
                    el.survey_data && el.survey_data.question1 > 0 ? el.survey_data.question1 : '-',
                  'Question 2': 'คุณพึงพอใจในการตอบคำถามได้อย่างครบถ้วนในระดับใด',
                  'Score - Question 2':
                    el.survey_data && el.survey_data.question2 > 0 ? el.survey_data.question2 : '-',
                  Suggestion:
                    el.survey_data && el.survey_data.suggestion ? el.survey_data.suggestion : '-'
                }
              : {
                  '(Group) NPS Ans': el.nps_group,
                  'NPS Ans': el.nps_score,
                  OSAT: el.osat_group,
                  'OSAT Flag': el.osat_score
                }),
            channel: el.channel,
            Agent: el.agent,
            Verbatim: el.verbatim,
            'Ticket No.': el.ticket_id
          }
        })

        const heading = [
          [
            'Survey Metadata - Start Date',
            'Survey Metadata - End Date',
            'Survey Metadata - Recorded Date',
            'Customer Name',
            'Distribution Channel',
            'Language',
            ...(this.isEmmaClinic
              ? [
                  'Question 1',
                  'Score - Question 1',
                  'Question 2',
                  'Score - Question 2',
                  'Suggestion'
                ]
              : ['(Group) NPS Ans', 'NPS Ans', 'OSAT', 'OSAT Flag']),
            'Channel',
            'Agent',
            'Verbatim',
            'Ticket No.'
          ]
        ]

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet([])
        XLSX.utils.sheet_add_aoa(ws, heading)
        XLSX.utils.sheet_add_json(ws, requests, { origin: 'A2', skipHeader: true })
        XLSX.utils.book_append_sheet(wb, ws)
        XLSX.writeFile(wb, `survey-by-agents.xlsx`)
      } catch (error) {
        console.error(error)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/survey/survey/index.vue